import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { X, Download, Loader2 } from 'lucide-react';
import { useIntl } from 'react-intl';
import { mediaUrl } from '../../utils/mediaUrl';
import { eventNameFor } from '../../utils/eventName';
import { CARD_W, CARD_H, renderShareCard } from '../../utils/shareCard';
import shareTemplate from '../../assets/speaker_share_template.png';

const BLUE = '#49ABDB';

const Overlay = styled.div`position:fixed;inset:0;background:rgba(15,23,42,.55);display:flex;align-items:flex-start;justify-content:center;overflow-y:auto;padding:2rem 1rem;z-index:1000;`;
const Modal = styled.div`background:#fff;max-width:46rem;width:100%;padding:1.75rem;box-shadow:0 25px 60px rgba(15,23,42,.35);position:relative;`;
const CloseBtn = styled.button`position:absolute;top:1rem;right:1rem;border:0;background:transparent;color:#5B6B78;cursor:pointer;padding:.25rem;&:hover{color:#111D4E;}`;
const ModalTitle = styled.h2`margin:0 0 .35rem;font-size:1.3rem;color:#111D4E;`;
const ModalHint = styled.p`margin:0 0 1.25rem;color:#5B6B78;font-size:.88rem;`;

const Body = styled.div`display:flex;gap:1.75rem;flex-wrap:wrap;@media(max-width:600px){flex-direction:column;}`;
const PreviewWrap = styled.div`flex:none;width:260px;@media(max-width:600px){width:100%;max-width:260px;margin:0 auto;}`;
const PreviewFrame = styled.div`position:relative;width:100%;aspect-ratio:${CARD_W}/${CARD_H};background:#eef2f7;box-shadow:0 10px 30px rgba(15,23,42,.18);overflow:hidden;`;
const PreviewCanvas = styled.canvas`width:100%;height:100%;display:block;`;
const PreviewOverlay = styled.div`position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,.7);color:#5B6B78;font-size:.85rem;gap:.4rem;`;
const Spin = styled(Loader2)`animation:spin 1s linear infinite;@keyframes spin{to{transform:rotate(360deg);}}`;

const Fields = styled.div`flex:1;min-width:240px;display:flex;flex-direction:column;gap:1rem;`;
const FieldLabel = styled.label`display:block;font-size:.78rem;font-weight:700;color:#5B6B78;margin-bottom:.35rem;`;
const QuoteInput = styled.textarea`width:100%;resize:vertical;min-height:4.2rem;padding:.6rem .7rem;border:1px solid #cbd5e1;font:inherit;font-size:.9rem;`;
const ReadonlyLine = styled.div`padding:.55rem .7rem;background:#f8fafc;border:1px solid #e2e8f0;color:#334155;font-size:.9rem;`;
const CharCount = styled.div`text-align:right;font-size:.72rem;color:#94a3b8;margin-top:.25rem;`;
const ErrorMsg = styled.div`font-size:.8rem;color:#b91c1c;`;
const Actions = styled.div`display:flex;gap:.6rem;margin-top:.25rem;`;
const DownloadBtn = styled.button`display:inline-flex;align-items:center;gap:.4rem;border:0;padding:.65rem 1.1rem;background:${BLUE};color:#fff;font-weight:700;font-size:.9rem;cursor:pointer;transition:background-color .3s;&:hover:not(:disabled){background:#0056b3;}&:disabled{opacity:.5;cursor:not-allowed;}`;

const QUOTE_MAX = 90;

// A speaker's photo may be an external URL from AI research/social profiles
// with no CORS headers (see mediaUrl.js) - drawing that onto the canvas
// still renders a preview fine, but taints the canvas so toBlob/toDataURL
// throw. That failure is caught and surfaced as fallback, not a hard error:
// the card still renders (with an initials avatar in place of the photo),
// so the speaker can still download something rather than nothing.
const ShareEngagementModal = ({ engagement, identity, onClose }) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const canvasRef = useRef(null);
  // Prefilled from the speaker's own "why I'm joining HEARTS" motivation
  // (same field AttendeeProfile.js shows under MotivationLabel) rather than
  // starting blank - it's already personal, on-topic copy the speaker wrote
  // themselves, and they can still edit or clear it here.
  const [quote, setQuote] = useState(() => (identity.motivation || '').slice(0, QUOTE_MAX));
  const [rendering, setRendering] = useState(true);
  const [downloadBlocked, setDownloadBlocked] = useState(false);
  const [error, setError] = useState('');

  const roleLabel = (r) => t(`speaker.engagementRole.${r}`, r ? r.charAt(0) + r.slice(1).toLowerCase() : r);

  const talkTitle = engagement.talkTitle || engagement.talkTitleSuggestion || engagement.sessionTitle || roleLabel(engagement.role);

  const eventDate = engagement.eventDateTime ? new Date(engagement.eventDateTime) : null;
  const dateStr = eventDate ? eventDate.toLocaleDateString(intl.locale, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
  // The speaker's own slot when the program gives one - a talk is much shorter than its session.
  const slotStart = engagement.talkStartTime || engagement.sessionStartTime;
  const slotEnd = engagement.talkStartTime ? engagement.talkEndTime : engagement.sessionEndTime;
  const timeStr = slotStart
    ? `${slotStart}${slotEnd ? `–${slotEnd}` : ''}`
    : (eventDate ? eventDate.toLocaleTimeString(intl.locale, { hour: '2-digit', minute: '2-digit' }) : '');
  const talkTime = [dateStr, timeStr].filter(Boolean).join(' · ');

  const subtitle = [identity.institution, [identity.city, identity.country].filter(Boolean).join(', ')].filter(Boolean).join(' / ');

  useEffect(() => {
    let cancelled = false;
    setRendering(true);
    setError('');
    renderShareCard(canvasRef.current, shareTemplate, {
      photoUrl: identity.photoUrl ? mediaUrl(identity.photoUrl) : null,
      name: identity.name,
      subtitle,
      quote,
      talkTitle,
      talkTime,
    })
      .catch(() => { if (!cancelled) setError(t('speaker.share.renderError', 'The preview could not be generated.')); })
      .finally(() => { if (!cancelled) setRendering(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, identity.photoUrl, identity.name, subtitle, talkTitle, talkTime]);

  const download = () => {
    const canvas = canvasRef.current;
    setDownloadBlocked(false);
    try {
      canvas.toBlob((blob) => {
        if (!blob) { setDownloadBlocked(true); return; }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const slug = (engagement.eventNameEN || engagement.eventName || 'hearts-engagement').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        a.download = `hearts-${slug || 'share'}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      }, 'image/png');
    } catch (e) {
      setDownloadBlocked(true);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseBtn type="button" onClick={onClose} aria-label={t('speaker.share.close', 'Close')}><X size={20} /></CloseBtn>
        <ModalTitle>{t('speaker.share.title', 'Share your HEARTS engagement')}</ModalTitle>
        <ModalHint>{t('speaker.share.hint', 'Download an image with your photo and details for {eventName} to post on social media.', { eventName: eventNameFor(intl.locale, engagement.eventName, engagement.eventNameEN) })}</ModalHint>
        <Body>
          <PreviewWrap>
            <PreviewFrame>
              <PreviewCanvas ref={canvasRef} width={CARD_W} height={CARD_H} />
              {rendering && <PreviewOverlay><Spin size={16} />{t('speaker.share.rendering', 'Rendering…')}</PreviewOverlay>}
            </PreviewFrame>
          </PreviewWrap>
          <Fields>
            <div>
              <FieldLabel>{t('speaker.share.quoteLabel', 'A short personal note (optional)')}</FieldLabel>
              <QuoteInput
                value={quote}
                maxLength={QUOTE_MAX}
                placeholder={t('speaker.share.quotePlaceholder', 'Why I am joining HEARTS…')}
                onChange={(e) => setQuote(e.target.value)}
              />
              <CharCount>{quote.length}/{QUOTE_MAX}</CharCount>
            </div>
            <div>
              <FieldLabel>{t('speaker.share.talkTitleLabel', 'Talk title')}</FieldLabel>
              <ReadonlyLine>{talkTitle}</ReadonlyLine>
            </div>
            <div>
              <FieldLabel>{t('speaker.share.talkTimeLabel', 'Date & time')}</FieldLabel>
              <ReadonlyLine>{talkTime || t('speaker.share.talkTimeUnset', 'To be announced')}</ReadonlyLine>
            </div>
            {error && <ErrorMsg>{error}</ErrorMsg>}
            {downloadBlocked && (
              <ErrorMsg>{t('speaker.share.downloadBlocked', "Your image couldn't be exported, likely because of your profile photo's source. Try again after re-uploading your photo on your profile.")}</ErrorMsg>
            )}
            <Actions>
              <DownloadBtn type="button" onClick={download} disabled={rendering || Boolean(error)}>
                <Download size={16} />{t('speaker.share.download', 'Download image')}
              </DownloadBtn>
            </Actions>
          </Fields>
        </Body>
      </Modal>
    </Overlay>
  );
};

export default ShareEngagementModal;
